class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    // Initialize/refresh timer after DOM is visible - with a longer delay to ensure content is loaded
    setTimeout(() => {
      this.initTimer();
      // Also update cart icon bubble when opening
      this.updateCartIconBubble();
    }, 200);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    if (this._timerInterval) {
      // Save current time before closing
      const el = this.querySelector('.cart-timer');
      if (el && this._currentRemaining !== undefined) {
        const persistCookie = el.getAttribute('data-persist-cookie') === 'true';
        const minutes = parseInt(el.getAttribute('data-minutes')) || 10;
        if (persistCookie) {
          this.setCookie('cart_timer_remaining', this._currentRemaining, minutes);
        }
      }
      clearInterval(this._timerInterval);
      this._timerInterval = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (sectionElement) {
        sectionElement.innerHTML =
            this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    }));

    setTimeout(() => {
      // Re-attach event listeners after content update
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.open();
      
      // Always restart timer after cart update, but only if cart is not empty
      if (parsedState.item_count > 0) {
        this.initTimer();
      }
      
      // Update cart icon bubble
      this.updateCartIconBubble();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer-customizer',
        section: 'cart-drawer-customizer',
        selector: '#CartDrawer'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateCartIconBubble() {
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble) {
      // Fetch cart data to get current item count
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          // Update the cart count bubble
          let cartCountBubble = cartIconBubble.querySelector('.cart-count-bubble');
          
          if (cart.item_count > 0) {
            // Create cart count bubble if it doesn't exist
            if (!cartCountBubble) {
              cartCountBubble = document.createElement('div');
              cartCountBubble.className = 'cart-count-bubble';
              cartIconBubble.appendChild(cartCountBubble);
            }
            
            cartCountBubble.style.display = 'flex';
            
            // Update or create the count span
            let countSpan = cartCountBubble.querySelector('span[aria-hidden="true"]');
            if (!countSpan) {
              countSpan = document.createElement('span');
              countSpan.setAttribute('aria-hidden', 'true');
              cartCountBubble.appendChild(countSpan);
            }
            countSpan.textContent = cart.item_count;
            
            // Update or create the visually hidden span
            let hiddenSpan = cartCountBubble.querySelector('span.visually-hidden');
            if (!hiddenSpan) {
              hiddenSpan = document.createElement('span');
              hiddenSpan.className = 'visually-hidden';
              cartCountBubble.appendChild(hiddenSpan);
            }
            hiddenSpan.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'} in cart`;
          } else {
            // Hide or remove cart count bubble when cart is empty
            if (cartCountBubble) {
              cartCountBubble.style.display = 'none';
            }
          }
        })
        .catch(e => {
          console.error('Error updating cart icon bubble:', e);
        });
    }
  }

  initTimer() {
    const el = this.querySelector('.cart-timer');
    if (!el) {
      console.log('Timer element not found, retrying in 100ms...');
      setTimeout(() => this.initTimer(), 100);
      return;
    }
    const timeSpan = el.querySelector('.cart-timer__time');
    if (!timeSpan) {
      console.log('Timer time span not found, retrying in 100ms...');
      setTimeout(() => this.initTimer(), 100);
      return;
    }

    const minutes = parseInt(el.getAttribute('data-minutes')) || 10;
    const persistCookie = el.getAttribute('data-persist-cookie') === 'true';
    const clearCartOnExpire = el.getAttribute('data-clear-cart-on-expire') === 'true';
    
    let remaining = minutes * 60;

    // Check for saved time in cookie if persistence is enabled
    if (persistCookie) {
      const savedTime = this.getCookie('cart_timer_remaining');
      if (savedTime) {
        remaining = parseInt(savedTime);
        // If saved time is expired, handle based on clear cart option
        if (remaining <= 0) {
          if (clearCartOnExpire) {
            // If cart should be cleared on expire and timer is expired, clear cart
            this.clearCart();
            return;
          } else {
            // Restart timer
            remaining = minutes * 60;
          }
        }
      }
    }

    // Clear previous - but save the current time first if it exists
    if (this._timerInterval) {
      // Save current remaining time before clearing
      if (persistCookie && this._currentRemaining !== undefined) {
        this.setCookie('cart_timer_remaining', this._currentRemaining, minutes);
      }
      clearInterval(this._timerInterval);
    }

    const tick = () => {
      const mm = Math.floor(remaining / 60);
      const ss = remaining % 60;
      timeSpan.textContent = `${mm}:${String(ss).padStart(2, '0')}`;
      
      // Store current remaining time in instance variable
      this._currentRemaining = remaining;
      
      // Save to cookie if persistence is enabled
      if (persistCookie) {
        this.setCookie('cart_timer_remaining', remaining, minutes);
      }
      
      remaining -= 1;
      
      if (remaining < 0) {
        if (clearCartOnExpire) {
          // Clear the cart when timer expires
          this.clearCart();
          // Don't restart timer if cart is cleared
          return;
        } else {
          // Restart timer
          remaining = minutes * 60;
          // Save the restarted timer to cookie if persistence is enabled
          if (persistCookie) {
            this.setCookie('cart_timer_remaining', remaining, minutes);
          }
        }
      }
    };

    tick();
    this._timerInterval = setInterval(tick, 1000);
  }

  clearCart() {
    // Clear all items from cart
    fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    .then(response => response.json())
    .then(() => {
      // Stop the timer
      if (this._timerInterval) {
        clearInterval(this._timerInterval);
        this._timerInterval = null;
      }
      
      // Clear timer cookie and current remaining time when cart is cleared
      this.deleteCookie('cart_timer_remaining');
      this._currentRemaining = undefined;
      
      // Reload the cart drawer to show empty state
      this.renderContents({ id: null, item_count: 0, sections: {} });
      
      // Update cart icon bubble immediately
      this.updateCartIconBubble();
      
      // Close the cart drawer after clearing
      setTimeout(() => {
        this.close();
      }, 1000);
    })
    .catch(error => {
      console.error('Error clearing cart:', error);
    });
  }

  setCookie(name, value, minutes) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (minutes * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  }

  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  deleteCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }

  // Method to restart timer when item is added to cart
  restartTimerOnAdd() {
    // Clear any existing timer
    if (this._timerInterval) {
      clearInterval(this._timerInterval);
      this._timerInterval = null;
    }
    
    // Clear any existing timer cookie to start fresh
    this.deleteCookie('cart_timer_remaining');
    
    // Restart timer
    setTimeout(() => {
      this.initTimer();
    }, 100);
  }
}

customElements.define('cart-drawer', CartDrawer);

// Wait for CartItems to be defined before creating CartDrawerItems
document.addEventListener('DOMContentLoaded', function() {
  if (typeof CartItems !== 'undefined') {
    class CartDrawerItems extends CartItems {
      constructor() {
        super();
        console.log('CartDrawerItems constructor called');
      }

      getSectionsToRender() {
        return [
          {
            id: 'cart-drawer-customizer',
            section: 'cart-drawer-customizer',
            selector: '#CartDrawer'
          }
        ];
      }

      onCartUpdate() {
        fetch('/cart?section_id=cart-drawer-customizer')
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('cart-drawer-items');
            if (sourceQty) {
              this.innerHTML = sourceQty.innerHTML;
            }
            // Update only the cart drawer content, not the entire wrapper
            const cartDrawerWrapper = document.querySelector('cart-drawer');
            if (cartDrawerWrapper) {
              // IMPORTANT: Save timer before replacing HTML
              const timerEl = cartDrawerWrapper.querySelector('.cart-timer');
              if (timerEl && cartDrawerWrapper._currentRemaining !== undefined) {
                const persistCookie = timerEl.getAttribute('data-persist-cookie') === 'true';
                const minutes = parseInt(timerEl.getAttribute('data-minutes')) || 10;
                if (persistCookie) {
                  cartDrawerWrapper.setCookie('cart_timer_remaining', cartDrawerWrapper._currentRemaining, minutes);
                }
              }
              
              const cartDrawerContent = html.querySelector('#CartDrawer');
              if (cartDrawerContent) {
                // Update only the inner content, preserve the wrapper structure
                const currentDrawerInner = cartDrawerWrapper.querySelector('.drawer__inner');
                const newDrawerInner = cartDrawerContent.querySelector('.drawer__inner');
                if (currentDrawerInner && newDrawerInner) {
                  currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
                }
                
                // Initialize timer after content update
                setTimeout(() => {
                  cartDrawerWrapper.initTimer();
                }, 100);
              }
            }
            
            // Update cart icon bubble immediately
            const cartDrawerForIcon = document.querySelector('cart-drawer');
            if (cartDrawerForIcon) {
              cartDrawerForIcon.updateCartIconBubble();
            }
          })
          .catch(e => {
            console.error(e);
          });
      }
    }

    customElements.define('cart-drawer-items', CartDrawerItems);
  } else {
    // Fallback: try again after a short delay
    setTimeout(() => {
      if (typeof CartItems !== 'undefined') {
        class CartDrawerItems extends CartItems {
          constructor() {
            super();
            console.log('CartDrawerItems constructor called');
          }

          getSectionsToRender() {
            return [
              {
                id: 'cart-drawer-customizer',
                section: 'cart-drawer-customizer',
                selector: '#CartDrawer'
              }
            ];
          }

          onCartUpdate() {
            fetch('/cart?section_id=cart-drawer-customizer')
              .then((response) => response.text())
              .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html');
                const sourceQty = html.querySelector('cart-drawer-items');
                if (sourceQty) {
                  this.innerHTML = sourceQty.innerHTML;
                }
                // Update only the cart drawer content, not the entire wrapper
                const cartDrawerWrapper = document.querySelector('cart-drawer');
                if (cartDrawerWrapper) {
                  // IMPORTANT: Save timer before replacing HTML
                  const timerEl = cartDrawerWrapper.querySelector('.cart-timer');
                  if (timerEl && cartDrawerWrapper._currentRemaining !== undefined) {
                    const persistCookie = timerEl.getAttribute('data-persist-cookie') === 'true';
                    const minutes = parseInt(timerEl.getAttribute('data-minutes')) || 10;
                    if (persistCookie) {
                      cartDrawerWrapper.setCookie('cart_timer_remaining', cartDrawerWrapper._currentRemaining, minutes);
                    }
                  }
                  
                  const cartDrawerContent = html.querySelector('#CartDrawer');
                  if (cartDrawerContent) {
                    // Update only the inner content, preserve the wrapper structure
                    const currentDrawerInner = cartDrawerWrapper.querySelector('.drawer__inner');
                    const newDrawerInner = cartDrawerContent.querySelector('.drawer__inner');
                    if (currentDrawerInner && newDrawerInner) {
                      currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
                    }
                    
                    // Initialize timer after content update
                    setTimeout(() => {
                      cartDrawerWrapper.initTimer();
                    }, 100);
                  }
                }
                
                // Update cart icon bubble immediately
                const cartDrawerForIcon2 = document.querySelector('cart-drawer');
                if (cartDrawerForIcon2) {
                  cartDrawerForIcon2.updateCartIconBubble();
                }
              })
              .catch(e => {
                console.error(e);
              });
          }
        }

        customElements.define('cart-drawer-items', CartDrawerItems);
      }
    }, 100);
  }
});
